import Button from "@components/common/Button";
import { DropdownOption } from "@/utils/types/dropDown";
import { PageHeader } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Alert, Card, message, Select, Spin } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { DropdownService, PrintFormatService } from "../../services/APIs";

const PrintPage = () => {
  const { schemaName, recordId } = useParams<{
    schemaName: string;
    recordId: string;
  }>();

  const [selectedLetterHeadId, setSelectedLetterHeadId] = useState<string>("");
  const [selectedPrintFormatId, setSelectedPrintFormatId] =
    useState<string>("");
  const [isPrinting, setIsPrinting] = useState(false);

  // Validate required parameters
  const isValidParams = useMemo(() => {
    return !!schemaName && !!recordId;
  }, [schemaName, recordId]);
  const {
    data: schemaOptions,
    isLoading: schemaLoading,
    error: schemaError,
  } = useQuery({
    queryKey: ["schema", schemaName],
    queryFn: () => DropdownService.getDropdownData("schema"),
    enabled: !!schemaName,
  });
  const {
    data: letterHeadOptions,
    isLoading: letterHeadLoading,
    error: letterHeadError,
  } = useQuery({
    queryKey: ["letterhead"],
    queryFn: () => DropdownService.getDropdownData("letterhead"),
  });
  useEffect(() => {
    if (
      letterHeadOptions &&
      Array.isArray(letterHeadOptions) &&
      !selectedLetterHeadId
    ) {
      const defaultLetterHead = letterHeadOptions.find(
        option => option.is_default
      );
      if (defaultLetterHead) {
        setSelectedLetterHeadId(String(defaultLetterHead.value ?? ""));
      }
    }
  }, [letterHeadOptions, selectedLetterHeadId]);
  const schemaId = useMemo(() => {
    if (!schemaOptions || !Array.isArray(schemaOptions)) {
      return "";
    }
    const foundSchema = schemaOptions.find(
      (schema: DropdownOption) => schema.label === schemaName
    );
    return foundSchema?.value ?? "";
  }, [schemaOptions, schemaName]);
  const {
    data: printFormatOptions,
    isLoading: printFormatLoading,
    error: printFormatError,
  } = useQuery({
    queryKey: ["printformat", schemaId],
    queryFn: () =>
      DropdownService.getDropdownData("printformat", {
        schema_id: schemaId,
      }),
    enabled: !!schemaId,
  });
  useEffect(() => {
    if (
      printFormatOptions &&
      Array.isArray(printFormatOptions) &&
      !selectedPrintFormatId
    ) {
      const defaultPrintFormat = printFormatOptions.find(
        option => option.is_default
      );
      if (defaultPrintFormat) {
        setSelectedPrintFormatId(String(defaultPrintFormat.value ?? ""));
      }
    }
  }, [printFormatOptions, selectedPrintFormatId]);
  const {
    data: previewPrintFormatHTML,
    isLoading: previewLoading,
    error: previewError,
  } = useQuery({
    queryKey: [
      "preview_print_format_html",
      schemaName,
      recordId,
      selectedLetterHeadId,
      selectedPrintFormatId,
    ],
    queryFn: () =>
      PrintFormatService.previewPrintFormatHTML({
        schema_name: schemaName ?? "",
        record_id: recordId ?? "",
        letter_head_id: selectedLetterHeadId,
        print_format_id: selectedPrintFormatId,
      }),
    enabled:
      !!schemaName &&
      !!recordId &&
      !!selectedLetterHeadId &&
      !!selectedPrintFormatId,
  });
  const handlePrint = useCallback(async () => {
    if (!selectedLetterHeadId || !selectedPrintFormatId) {
      message.error("Please select both Letter Head and Print Format");
      return;
    }

    setIsPrinting(true);
    try {
      const pdfBlob = await PrintFormatService.renderPrintFormatPDF({
        schema_name: schemaName ?? "",
        record_id: recordId ?? "",
        letter_head_id: selectedLetterHeadId,
        print_format_id: selectedPrintFormatId,
      });

      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `print_${schemaName}_${recordId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success("PDF downloaded successfully");
    } catch (error) {
      message.error("Failed to generate PDF. Please try again.");
      console.error("Print error:", error);
    } finally {
      setIsPrinting(false);
    }
  }, [selectedLetterHeadId, selectedPrintFormatId, schemaName, recordId]);

  // Computed values
  const isFormValid = useMemo(() => {
    return !!selectedLetterHeadId && !!selectedPrintFormatId;
  }, [selectedLetterHeadId, selectedPrintFormatId]);

  const isLoading = useMemo(() => {
    return (
      schemaLoading ||
      letterHeadLoading ||
      printFormatLoading ||
      previewLoading ||
      isPrinting
    );
  }, [
    schemaLoading,
    letterHeadLoading,
    printFormatLoading,
    previewLoading,
    isPrinting,
  ]);

  const hasError = useMemo(() => {
    return schemaError ?? letterHeadError ?? printFormatError ?? previewError;
  }, [schemaError, letterHeadError, printFormatError, previewError]);

  // Early return for invalid params
  if (!isValidParams) {
    return (
      <Card>
        <PageHeader className="custom-page-header" title="Print" />
        <div className="p-4">
          <Alert
            message="Invalid Parameters"
            description="Schema name and record ID are required."
            type="error"
            showIcon
          />
        </div>
      </Card>
    );
  }

  return (
    <Card title={<PageHeader className="custom-page-header" title="Print" />}>
      <div className="space-y-4 p-4">
        {hasError && (
          <Alert
            message="Error Loading Data"
            description="Failed to load some data. Please refresh the page and try again."
            type="error"
            showIcon
            closable
          />
        )}

        <div className="space-y-2">
          <label className="block text-sm font-medium">Letter Head</label>
          <Select
            options={
              Array.isArray(letterHeadOptions)
                ? letterHeadOptions.map((option: DropdownOption) => ({
                    label: option.label,
                    value: option.id || option.value,
                  }))
                : []
            }
            value={selectedLetterHeadId}
            onChange={setSelectedLetterHeadId}
            loading={letterHeadLoading}
            placeholder="Select Letter Head"
            className="w-full"
            allowClear
            disabled={letterHeadLoading}
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium">Print Format</label>
          <Select
            options={
              Array.isArray(printFormatOptions)
                ? printFormatOptions.map((option: DropdownOption) => ({
                    label: option.label,
                    value: option.id || option.value,
                  }))
                : []
            }
            value={selectedPrintFormatId}
            onChange={setSelectedPrintFormatId}
            loading={printFormatLoading}
            placeholder="Select Print Format"
            className="w-full"
            allowClear
            disabled={printFormatLoading || !schemaId}
          />
        </div>

        {previewLoading && (
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center justify-center py-8">
              <Spin size="large" />
              <span className="ml-2">Loading preview...</span>
            </div>
          </div>
        )}

        {previewError && (
          <Alert
            message="Preview Error"
            description="Failed to load preview. Please check your selections and try again."
            type="warning"
            showIcon
          />
        )}

        {previewPrintFormatHTML && !previewLoading && (
          <div dangerouslySetInnerHTML={{ __html: previewPrintFormatHTML }} />
        )}

        <Button
          type="primary"
          onClick={handlePrint}
          disabled={!isFormValid || isLoading}
          loading={isPrinting}
          className="mt-4"
        >
          {isPrinting ? "Generating PDF..." : "Print"}
        </Button>
      </div>
    </Card>
  );
};

export default PrintPage;
